import { useEffect, useRef, useCallback, useState } from 'react';
import { apiFetchUrl, resolveWebSocketUrl, getValidAccessToken } from './api';

interface UseNotificationPushOptions {
  enabled?: boolean;
  userId?: string;
  organizationId?: string;
  portalType?: string;
  onNotification?: () => void;
}

export function useNotificationPush({
  enabled = true,
  userId,
  organizationId,
  portalType,
  onNotification,
}: UseNotificationPushOptions) {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const connectGenerationRef = useRef(0);
  const onNotificationRef = useRef(onNotification);
  const [connected, setConnected] = useState(false);
  onNotificationRef.current = onNotification;

  const connect = useCallback(async () => {
    if (!enabled || !userId || !organizationId || !portalType) return;
    if (typeof WebSocket === 'undefined') return;

    const generation = ++connectGenerationRef.current;

    const token = await getValidAccessToken();
    // StrictMode remount / newer connect superseded this attempt
    if (generation !== connectGenerationRef.current) return;
    if (!token) return;

    // Replace any existing socket before opening another
    if (wsRef.current) {
      wsRef.current.onclose = null;
      wsRef.current.close();
      wsRef.current = null;
    }

    const query = new URLSearchParams({ userId, organizationId, portalType });
    const url = resolveWebSocketUrl(apiFetchUrl(`/ws/notifications?${query.toString()}`));

    try {
      const ws = new WebSocket(url);
      if (generation !== connectGenerationRef.current) {
        ws.close();
        return;
      }
      wsRef.current = ws;

      ws.onopen = () => {
        if (generation === connectGenerationRef.current) {
          setConnected(true);
        }
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data as string);
          if (msg.type === 'notification') {
            onNotificationRef.current?.();
          }
        } catch {
          // ignore malformed payloads
        }
      };

      ws.onclose = (ev) => {
        if (wsRef.current === ws) wsRef.current = null;
        if (generation === connectGenerationRef.current) {
          setConnected(false);
        }
        if (enabled && generation === connectGenerationRef.current && ev.code !== 1008) {
          reconnectTimerRef.current = setTimeout(() => {
            void connect();
          }, 5000);
        }
      };

      ws.onerror = () => {
        ws.close();
      };
    } catch {
      // WebSocket unavailable — polling remains the fallback
    }
  }, [enabled, userId, organizationId, portalType]);

  useEffect(() => {
    if (!enabled) {
      setConnected(false);
      return;
    }
    void connect();
    return () => {
      connectGenerationRef.current += 1;
      setConnected(false);
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [enabled, connect]);

  return { connected };
}
